import { ResultOf } from 'gql.tada';
import debounce from 'lodash/debounce';
import { useEffect, useRef } from 'react';

import {
  ComboboxOption,
  Icon,
  LoadingIcon,
  Stack,
  useComboboxInputValue,
  useSetComboboxInputValue,
} from '@eluve/components';

import { UserOption } from './UserOption';
import { tenantUserOptionFragment } from './operations';

export interface UserComboboxContentProps {
  selectedUser: ResultOf<typeof tenantUserOptionFragment> | null;
  searchResults: ResultOf<typeof tenantUserOptionFragment>[];
  loading: boolean;
  handleUserSelected: (userId: string) => void;
  tenantId: string;
  debouncedSearch: ReturnType<typeof debounce>;
  setSearchResults: React.Dispatch<
    React.SetStateAction<ResultOf<typeof tenantUserOptionFragment>[]>
  >;
}

export const UserComboboxContent: React.FC<UserComboboxContentProps> = ({
  selectedUser,
  searchResults,
  loading,
  handleUserSelected,
  tenantId: _tenantId,
  debouncedSearch,
  setSearchResults,
}) => {
  const inputValue = useComboboxInputValue();
  const setInputValue = useSetComboboxInputValue();

  const prevInputRef = useRef(inputValue);

  useEffect(
    function searchOnInputChange() {
      if (prevInputRef.current !== inputValue) {
        prevInputRef.current = inputValue;

        if (!inputValue || inputValue.length < 2) {
          setSearchResults([]);
        } else {
          debouncedSearch(inputValue);
        }
      }
    },
    [inputValue, debouncedSearch, setSearchResults],
  );

  useEffect(
    function clearResultsOnMount() {
      setSearchResults([]);
    },
    [setSearchResults],
  );

  return (
    <>
      {loading && (
        <Stack
          className="py-4"
          fullWidth={false}
          gap={2}
          align="center"
          justify="center"
        >
          <LoadingIcon size="xl" className="text-bg-accent-strong-primary" />
          <span className="mt-2 text-text-secondary">Searching users...</span>
        </Stack>
      )}

      {!loading && inputValue.length > 0 && inputValue.length < 2 && (
        <div className="px-4 py-2 text-text-tertiary">
          Type at least 2 characters to search
        </div>
      )}

      {!loading && inputValue.length >= 2 && searchResults.length === 0 && (
        <div className="px-4 py-2 text-text-tertiary">No users found</div>
      )}

      {!loading &&
        searchResults.length > 0 &&
        searchResults.map((user) => {
          return (
            <ComboboxOption
              key={user.id}
              onSelect={() => {
                handleUserSelected(user.id);
                setInputValue('');
                setSearchResults([]);
              }}
            >
              <Stack direction="horizontal" gap={2}>
                <div className="flex-1">
                  <UserOption {...user} />
                </div>
                {selectedUser?.id === user.id && (
                  <Icon
                    name="Check"
                    size="xl"
                    className="w-max text-bg-accent-strong-primary"
                  />
                )}
              </Stack>
            </ComboboxOption>
          );
        })}
    </>
  );
};
